import { useState } from "react";
import React from "react";
import { pdf, Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import CarnetFrontal from "../../styles/assets/img/CarnetFrontal.jpg";

const W = 93.0;
const H = 126.0;
const HALF = 63.0;
const PX_X = W / 727;
const PX_Y = H / 985;
const CARD_W_MM = W;
const CARD_H_MM = HALF;
const FULL_H_MM = H;

const FRONT = {
  nombre: { top: 310 * PX_Y, left: 160 * PX_X },
  cedula: { top: 350 * PX_Y, left: 160 * PX_X },
  codigo: { top: 390 * PX_Y, left: 160 * PX_X },
};

const BACK = {
  ubicacion:    { top: (390 - 350) * PX_Y, left: 320 * PX_X },
  oficina:      { top: (455 - 322) * PX_Y, left: 300 * PX_X },
  arrendatario: { top: (482 - 280) * PX_Y, left: 370 * PX_X },
};

const UBIC_MAX_LINE1 = 30;
const UBIC_TOP1  = `${((390 - 370) * PX_Y).toFixed(3)}mm`;
const UBIC_LEFT1 = `${(320 * PX_X).toFixed(3)}mm`;
const UBIC_TOP2  = `${((390 - 370) * PX_Y + 7.1).toFixed(3)}mm`;
const UBIC_LEFT2 = "5.000mm";

const ARREN_MAX_LINE1 = 30;
const ARREN_TOP1  = `${((482 - 295) * PX_Y).toFixed(3)}mm`;
const ARREN_LEFT1 = `${(350 * PX_X).toFixed(3)}mm`;
const ARREN_TOP2  = `${((482 - 295) * PX_Y + 7.1).toFixed(3)}mm`;
const ARREN_LEFT2 = "5.000mm";

const mm = (v) => `${v.toFixed(3)}mm`;

const MM_TO_PT = 2.8346;
const W_PT    = W    * MM_TO_PT;
const H_PT    = H    * MM_TO_PT;
const HALF_PT = HALF * MM_TO_PT;
const pt = (mmVal) => mmVal * MM_TO_PT;

const pdfStyles = StyleSheet.create({
  page:  { width: W_PT, height: H_PT, backgroundColor: "#ffffff", position: "relative" },
  field: { position: "absolute", fontSize: 7.5, fontFamily: "Helvetica", color: "#000000" },
});

// ── Datos vacíos para resetear el formulario ──────────────────────────────────
const dataVacia = {
  nombre: "", cedula: "", codigo: "",
  ubicacionFinca: "", oficinaAutorizada: "", controlArrendatario: "",
};

// ── PDF ───────────────────────────────────────────────────────────────────────
function CarnetPDF({ data }) {
  const split = (valor, max) => ({
    l1: valor ? valor.slice(0, max) : "",
    l2: valor ? valor.slice(max).trim() : "",
  });
  const ub = split(data.ubicacionFinca,      UBIC_MAX_LINE1);
  const ar = split(data.controlArrendatario, ARREN_MAX_LINE1);

  return (
    <Document>
      <Page size={[W_PT, H_PT]} style={pdfStyles.page} rotate={180}>
        <View style={{ position: "absolute", top: 0, left: 0, width: W_PT, height: HALF_PT, transform: "rotate(180deg)" }}>
          {data.ubicacionFinca && (
            <>
              <Text style={[pdfStyles.field, { top: pt(parseFloat(UBIC_TOP1)), left: pt(BACK.ubicacion.left) }]}>{ub.l1}</Text>
              {ub.l2 && <Text style={[pdfStyles.field, { top: pt(parseFloat(UBIC_TOP2)), left: pt(parseFloat(UBIC_LEFT2)) }]}>{ub.l2}</Text>}
            </>
          )}
          {data.oficinaAutorizada && (
            <Text style={[pdfStyles.field, { top: pt(BACK.oficina.top), left: pt(BACK.oficina.left) }]}>{data.oficinaAutorizada}</Text>
          )}
          {data.controlArrendatario && (
            <>
              <Text style={[pdfStyles.field, { top: pt(parseFloat(ARREN_TOP1)), left: pt(BACK.arrendatario.left) }]}>{ar.l1}</Text>
              {ar.l2 && <Text style={[pdfStyles.field, { top: pt(parseFloat(ARREN_TOP2)), left: pt(parseFloat(ARREN_LEFT2)) }]}>{ar.l2}</Text>}
            </>
          )}
        </View>
        {data.nombre && <Text style={[pdfStyles.field, { top: pt(HALF + FRONT.nombre.top), left: pt(FRONT.nombre.left) }]}>{data.nombre}</Text>}
        {data.cedula && <Text style={[pdfStyles.field, { top: pt(HALF + FRONT.cedula.top), left: pt(FRONT.cedula.left) }]}>{data.cedula}</Text>}
        {data.codigo && <Text style={[pdfStyles.field, { top: pt(HALF + FRONT.codigo.top), left: pt(FRONT.codigo.left) }]}>{data.codigo}</Text>}
      </Page>
    </Document>
  );
}

async function prepararImpresion(data, setPdfUrl, setShowModal) {
  const blob = await pdf(<CarnetPDF data={data} />).toBlob();
  setPdfUrl(URL.createObjectURL(blob));
  setShowModal(true);
}

function ejecutarImpresion(pdfUrl, onDone) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = pdfUrl;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        if (onDone) onDone();
      }, 1500);
    }, 500);
  };
}

// ── Componentes helper ────────────────────────────────────────────────────────
function UbicacionSplit({ value, className }) {
  if (!value) return null;
  const line1 = value.slice(0, UBIC_MAX_LINE1);
  const rest  = value.slice(UBIC_MAX_LINE1).trim();
  return (
    <>
      <span className={className} style={{ position: "absolute", top: UBIC_TOP1, left: UBIC_LEFT1, whiteSpace: "nowrap" }}>{line1}</span>
      {rest && <span className={className} style={{ position: "absolute", top: UBIC_TOP2, left: UBIC_LEFT2, whiteSpace: "nowrap" }}>{rest}</span>}
    </>
  );
}

function ArrendatarioSplit({ value, className }) {
  if (!value) return null;
  const line1 = value.slice(0, ARREN_MAX_LINE1);
  const rest  = value.slice(ARREN_MAX_LINE1).trim();
  return (
    <>
      <span className={className} style={{ position: "absolute", top: ARREN_TOP1, left: ARREN_LEFT1, whiteSpace: "nowrap" }}>{line1}</span>
      {rest && <span className={className} style={{ position: "absolute", top: ARREN_TOP2, left: ARREN_LEFT2, whiteSpace: "nowrap" }}>{rest}</span>}
    </>
  );
}

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: #f0f4f0; }

  .app-wrapper { min-height: 100vh; display: grid; grid-template-columns: 400px 1fr; }

  .form-panel {
    background: #1a1a2e; padding: 32px 28px;
    display: flex; flex-direction: column; gap: 16px; overflow-y: auto;
  }
  .form-panel h1 { font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 800; color: #fff; line-height: 1.25; }
  .form-panel h1 span { color: #d4a017; }

  .form-section {
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    border-radius: 9px; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px;
  }
  .form-section h3 {
    font-family: 'Syne', sans-serif; font-size: 9px; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase; color: #d4a017;
  }
  .form-row { display: flex; flex-direction: column; gap: 3px; }
  .form-row label { font-size: 9px; color: rgba(255,255,255,0.4); letter-spacing: 0.8px; text-transform: uppercase; }
  .form-row input {
    background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.13);
    border-radius: 5px; padding: 7px 10px; color: #fff;
    font-family: 'DM Sans', sans-serif; font-size: 13px; outline: none; transition: border-color .2s;
  }
  .form-row input:focus { border-color: #d4a017; }

  .btn-print {
    background: #d4a017; color: #1a1a2e; border: none; border-radius: 7px; padding: 12px 18px;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1px;
    text-transform: uppercase; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    box-shadow: 0 4px 14px rgba(212,160,23,0.3); transition: transform .15s;
  }
  .btn-print:hover { transform: translateY(-1px); }
  .btn-print:active { transform: translateY(0); }

  .hint { font-size: 10px; color: rgba(255,255,255,0.28); line-height: 1.65; }
  .hint b { color: rgba(255,255,255,0.5); }

  .preview-panel {
    background: #d8e0d8; display: flex; flex-direction: column;
    align-items: center; justify-content: flex-start; padding: 36px 32px; gap: 28px; overflow-y: auto;
  }
  .preview-section { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
  .prev-lbl { font-family: 'Syne', sans-serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #2d6a4f; }

  .pv-field {
    position: absolute; font-family: Arial, sans-serif; font-size: 9pt;
    color: #0000cc; font-weight: 700; white-space: nowrap; line-height: 1.55;
    pointer-events: none; z-index: 2;
  }

  /* ── MODAL ── */
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.75);
    z-index: 9999; display: flex; align-items: center; justify-content: center;
  }
  .modal-box {
    background: #1a1a2e; border: 1px solid rgba(255,255,255,0.15);
    border-radius: 12px; padding: 28px 32px; max-width: 420px; width: 90%;
    display: flex; flex-direction: column; gap: 16px;
  }
  .modal-box h2 { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 800; color: #fff; }
  .modal-box h2 span { color: #d4a017; }
  .modal-step { display: flex; align-items: flex-start; gap: 12px; font-size: 13px; color: rgba(255,255,255,0.75); line-height: 1.5; }
  .modal-step-num {
    background: #d4a017; color: #1a1a2e; font-family: 'Syne', sans-serif; font-weight: 800;
    font-size: 12px; width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px;
  }
  .modal-step b { color: #fff; }
  .modal-actions { display: flex; gap: 10px; margin-top: 4px; }
  .modal-btn-primary {
    flex: 1; border: none; border-radius: 7px; padding: 11px 16px;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase; cursor: pointer;
    background: #d4a017; color: #1a1a2e;
  }
  .modal-btn-secondary {
    background: transparent; color: rgba(255,255,255,0.5);
    border: 1px solid rgba(255,255,255,0.2); border-radius: 7px; padding: 11px 16px;
    font-family: 'Syne', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; text-decoration: none;
    display: flex; align-items: center; justify-content: center;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 900px) {
    .app-wrapper { grid-template-columns: 1fr; }
    .form-panel { padding: 20px 16px; }
    .preview-panel { padding: 24px 16px; }
    .preview-scale-wrapper { transform-origin: top left; }
  }
  @media (max-width: 480px) {
    .form-panel h1 { font-size: 17px; }
    .form-section { padding: 12px 12px; }
    .modal-box { padding: 20px 18px; }
    .modal-actions { flex-direction: column; }
    .modal-btn-primary, .modal-btn-secondary { width: 100%; }
  }

  @media print { .app-wrapper { display: none !important; } }
`;

// ── COMPONENTE PRINCIPAL ──────────────────────────────────────────────────────
export default function PrintCarnet() {
  const [data, setData] = useState({
    nombre:              "",
    cedula:              "",
    codigo:              "",
    ubicacionFinca:      "",
    oficinaAutorizada:   "",
    controlArrendatario: "",
  });
  const [showModal,   setShowModal]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pdfUrl,      setPdfUrl]      = useState(null);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const previewScale = typeof window !== "undefined" && window.innerWidth < 500
    ? (window.innerWidth - 48) / (parseFloat(mm(CARD_W_MM)) * 3.7795)
    : 1;

  return (
    <>
      <style>{css}</style>

      <div className="app-wrapper">

        {/* ── FORMULARIO ── */}
        <div className="form-panel">
          <h1>Carné JNFA<br /><span>Ferias del Agricultor</span></h1>

          <div className="form-section">
            <h3>Sección Datos Personales</h3>
            {[
              ["nombre", "Nombre completo"],
              ["cedula", "Número de cédula"],
              ["codigo", "Código de productor"],
            ].map(([k, lbl]) => (
              <div className="form-row" key={k}>
                <label>{lbl}</label>
                <input value={data[k]} onChange={set(k)} placeholder={lbl} />
              </div>
            ))}
          </div>

          <div className="form-section">
            <h3>Sección Dirección</h3>
            {[
              ["ubicacionFinca",      "Ubicación de la finca"],
              ["oficinaAutorizada",   "Oficina autorizada"],
              ["controlArrendatario", "Control de arrendatario"],
            ].map(([k, lbl]) => (
              <div className="form-row" key={k}>
                <label>{lbl}</label>
                <input value={data[k]} onChange={set(k)} placeholder={lbl} />
              </div>
            ))}
          </div>

          <button className="btn-print" onClick={() => prepararImpresion(data, setPdfUrl, setShowModal)}>
            🖨️&nbsp; Imprimir Carné
          </button>

          <p className="hint">
            Genera una sola hoja <b>93 × 126mm</b>.<br />
            Cara frontal abajo · Cara trasera arriba (invertida).
          </p>
        </div>

        {/* ── PREVIEW ── */}
        <div className="preview-panel">
          <div className="preview-section">
            <p className="prev-lbl">Vista previa — Carné completo (tamaño real)</p>
            <h4 style={{ color: "red" }}>Introducir en impresora en esta misma dirección</h4>

            <div className="preview-scale-wrapper" style={{
              transform: previewScale < 1 ? `scale(${previewScale})` : "none",
            }}>
              <div style={{
                position: "relative", width: mm(CARD_W_MM), height: mm(FULL_H_MM),
                overflow: "hidden", boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
                outline: "1.5px solid rgba(212,160,23,0.6)", transform: "rotate(180deg)",
              }}>
                <img src={CarnetFrontal} alt="Carné completo" style={{
                  position: "absolute", top: 0, left: 0,
                  width: mm(CARD_W_MM), height: mm(FULL_H_MM), objectFit: "fill", display: "block",
                }} />

                <div style={{
                  position: "absolute", top: 0, left: 0,
                  width: mm(CARD_W_MM), height: mm(CARD_H_MM),
                  transform: "rotate(180deg)", transformOrigin: "center center",
                }}>
                  <UbicacionSplit value={data.ubicacionFinca || "Ubicación de la finca"} className="pv-field" />
                  <span className="pv-field" style={{ top: mm(BACK.oficina.top), left: mm(BACK.oficina.left) }}>
                    {data.oficinaAutorizada || "Oficina autorizada"}
                  </span>
                  <ArrendatarioSplit value={data.controlArrendatario || "Control de arrendatario"} className="pv-field" />
                </div>

                <span className="pv-field" style={{ top: `calc(${mm(CARD_H_MM)} + ${mm(FRONT.nombre.top)})`, left: mm(FRONT.nombre.left) }}>
                  {data.nombre || "Nombre"}
                </span>
                <span className="pv-field" style={{ top: `calc(${mm(CARD_H_MM)} + ${mm(FRONT.cedula.top)})`, left: mm(FRONT.cedula.left) }}>
                  {data.cedula || "Cédula"}
                </span>
                <span className="pv-field" style={{ top: `calc(${mm(CARD_H_MM)} + ${mm(FRONT.codigo.top)})`, left: mm(FRONT.codigo.left) }}>
                  {data.codigo || "Código"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── MODAL INSTRUCCIONES ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h2>Imprimir Carné <span>JNFA</span></h2>
            <div className="modal-step">
              <div className="modal-step-num">1</div>
              <div>El PDF ya tiene el tamaño <b>93 × 126mm</b> configurado.</div>
            </div>
            <div className="modal-step">
              <div className="modal-step-num">2</div>
              <div>Descárgalo y ábrelo con <b>Adobe Reader</b> → imprime sin configurar nada.</div>
            </div>
            <div className="modal-step">
              <div className="modal-step-num">3</div>
              <div>O usa <b>"Imprimir ahora"</b> y verifica tamaño <b>93 × 126mm</b>, escala <b>100%</b>.</div>
            </div>
            <div className="modal-actions">
              <button className="modal-btn-primary" onClick={() => {
                setShowModal(false);
                ejecutarImpresion(pdfUrl, () => setShowConfirm(true));
              }}>
                🖨️ Imprimir ahora
              </button>
              <a className="modal-btn-secondary" href={pdfUrl} download="carnet_JNFA.pdf" onClick={() => setShowModal(false)}>
                📥 Descargar PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL CONFIRMACIÓN POST-IMPRESIÓN ── */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ alignItems: "center", textAlign: "center" }}>
            <div style={{ fontSize: 40 }}>🖨️</div>
            <h2>¿La impresión fue <span>exitosa</span>?</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", lineHeight: 1.6 }}>
              Si el carné se imprimió correctamente, los campos se limpiarán.<br />
              Si hubo algún problema, se mantendrán para volver a intentarlo.
            </p>
            <div className="modal-actions" style={{ width: "100%" }}>
              <button
                className="modal-btn-primary"
                style={{ background: "#27ae60" }}
                onClick={() => { setData(dataVacia); setShowConfirm(false); }}
              >
                ✅ Sí, limpiar campos
              </button>
              <button
                className="modal-btn-secondary"
                style={{ color: "#e74c3c", borderColor: "#e74c3c" }}
                onClick={() => setShowConfirm(false)}
              >
                ❌ No, mantener datos
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
